import { useAccountActions } from '../../redux/actions/accountActions';
import { useUserActions } from '../../redux/actions/userActions';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { useUserState } from '../../redux/reducers/userReducer';
import { useApi } from '../api/useApi';
import { AccountInfo, ClientPortal, ClientPortalUser } from '../types';

export interface UserAccountService {
    account: AccountInfo | undefined;
    authenticatedUser: ClientPortalUser | undefined;
    loadPortalAccountAsync: (
        portal: ClientPortal,
        user: ClientPortalUser,
        signal: AbortSignal,
    ) => Promise<void>;
    clearAccount: () => void;
}

export const useUserAccountService = (): UserAccountService => {
    const { portalAccount: account } = useAccountState();
    const { authenticatedUser } = useUserState();
    const accountActions = useAccountActions();
    const userActions = useUserActions();
    const api = useApi<AccountInfo>('api/Account');

    const clearAccount = () => {
        accountActions.clearAccounts();
        accountActions.clearPrimaryAccount();
        userActions.setAuthenticatedUser(undefined);
        userActions.setUsers([]);
    };

    const loadPortalAccountAsync = async (
        portal: ClientPortal,
        user: ClientPortalUser,
        signal: AbortSignal,
    ): Promise<void> => {
        if (!portal.accountId?.trim() || !portal.portalId?.trim()
            || !user.userId?.trim() || user.portalId !== portal.portalId) {
            throw new Error('A valid portal and authenticated portal user are required to load the account.');
        }

        signal.throwIfAborted();
        const loadedAccount: AccountInfo | undefined = await api.getAsync(
            `accountInfo/${encodeURIComponent(portal.accountId)}`,
        );
        signal.throwIfAborted();

        if (!loadedAccount || loadedAccount.accountId !== portal.accountId) {
            throw new Error('The portal account could not be loaded.');
        }

        accountActions.setPortalAccount(loadedAccount);
        userActions.setAuthenticatedUser(user);
    };

    return { account, authenticatedUser, loadPortalAccountAsync, clearAccount };
};
